import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, FormGroup, Label, Input } from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import axios from 'axios';
import { Toggle } from 'rsuite';
import {  Autocomplete } from '@react-google-maps/api';
import { setActiveFlow, setCurrentStep, setIsPickupRequired, setFormData } from 'store/slices/flowSlice';
import { countryOptions } from 'data/countries';
import { useTranslation } from "react-i18next";


const airportCountries = countryOptions.filter(country => country.airport);



const AirForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const validationSchema = Yup.object({
    fromLocation: Yup.object().required(t('From location is required')),
    fromAddress: Yup.string().when('pickupRequired', {
      is: true,
      then: () => Yup.string().required(t('Address is required when pickup is required')),
      otherwise: () => Yup.string().nullable(),
    }),
    fromAirport: Yup.object().when('pickupRequired', {
      is: false,
      then: () => Yup.object().required(t('From airport is required when pickup is not required')),
      otherwise: () => Yup.object().nullable(),
    }),
    toLocation: Yup.object().required(t('To location is required')),
    toAddress: Yup.string().when('deliveredToRecipient', {
      is: true,
      then: () => Yup.string().required(t('Address is required when delivery to recipient is required')),
      otherwise: () => Yup.string().nullable(),
    }),
    toAirport: Yup.object().when('deliveredToRecipient', {
      is: false,
      then: () => Yup.object().required(t('To airport is required when delivery to recipient is not required')),
      otherwise: () => Yup.object().nullable(),
    }),
  });
  const [pickupRequired, setPickupRequired] = useState(false);
  const [deliveredToRecipient, setDeliveredToRecipient] = useState(false);
  const [selectedFromCountry, setSelectedFromCountry] = useState(null);
  const [selectedToCountry, setSelectedToCountry] = useState(null);
  const [fromAirports, setFromAirports] = useState([]);
  const [toAirports, setToAirports] = useState([]);
  const fromCityZipRef = useRef(null);
  const toCityZipRef = useRef(null);


  const handleSubmit = (values) => {
    dispatch(setFormData(values));
    dispatch(setActiveFlow('air'));
    dispatch(setIsPickupRequired(pickupRequired));
    dispatch(setCurrentStep('1'));
  };

  const fetchAirports = async (countryCode, setAirports) => {
    try {
      const response = await axios.post(`http://101.46.141.94:8069/api/airports?country=${countryCode}`);


      const airportOptions = response.data.map(airport => {
        const code = airport.iata === "\\N" ? airport.icao : airport.iata;
        return {
          value: `${countryCode}-${airport.city}, ${code}`,
          label: `${countryCode}-${airport.city}, ${code}`,
        };
      });

      setAirports(airportOptions);
    } catch (error) {
      console.error('Error fetching airports: ', error);
      setAirports([]);
    }
  };

  const handleCountryChange = (field, value, setFieldValue) => {
    setFieldValue(field, value);
    const countryCode = value ? value.value : null;
    if (field === 'fromLocation') {
      setSelectedFromCountry(countryCode);
      setFieldValue('fromAirport', null);
      if (countryCode) {
        fetchAirports(countryCode, setFromAirports);
      }
    } else if (field === 'toLocation') {
      setSelectedToCountry(countryCode);
      setFieldValue('toAirport', null);
      if (countryCode) {
        fetchAirports(countryCode, setToAirports);
      }
    }
  };

  const handlePlaceSelect = (autocomplete, setFieldValue, field) => {
    const place = autocomplete.getPlace();
    if (place && place.formatted_address) {
      sessionStorage.setItem(field, place?.formatted_address);
      setFieldValue(field, place.formatted_address);
    } else {
      alert(t('Unable to retrieve a valid address. Please try selecting again.'));
    }
  };

  return (
    <Formik
      initialValues={{
        fromLocation: '',
        fromAddress: '',
        toLocation: '',
        toAddress: '',
        deliveredToRecipient: false,
        pickupRequired: false
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, errors, values, touched }) => (
        <Form>
          <Row>
            <Col sm="12">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label mb-2 fs-custom fw-bold text-dark e2g-form-size" for="fromLocation">{t("From")}</Label>
                <FormGroup switch>
                  <Toggle
                    checked={pickupRequired}
                    onChange={(value) => {
                      setFieldValue('pickupRequired', value);
                      setPickupRequired(value);
                    }}
                  />
                  <Label check className='fs-7'>{t('Pickup Required')}</Label>
                </FormGroup>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Select
                  className='bg-white rounded e2g-form-input border-0'
                  id="fromLocation"
                  options={airportCountries}
                  onChange={option => handleCountryChange('fromLocation', option, setFieldValue)}
                  placeholder={t("Select Location")}
                />
                {errors.fromLocation && touched.fromLocation ? (
                  <div className="e2g-error-text text-danger">{errors.fromLocation}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                {pickupRequired ? (
                  <Autocomplete
                    onLoad={(autocomplete) => (fromCityZipRef.current = autocomplete)}
                    key={'from'+values?.fromLocation?.value}
                    onPlaceChanged={() =>
                      handlePlaceSelect(fromCityZipRef.current, setFieldValue, 'fromAddress')
                    }
                    options={{
                      types: ['address'], // Restrict to address results
                      componentRestrictions: {
                        country: values.fromLocation ? values?.fromLocation?.value : '',
                      },
                    }}
                  >
                    <Field
                      name="fromAddress"
                      as={Input}
                      placeholder={t("Provide Full Address")}
                      disabled={!(values?.fromLocation?.value)}
                    />
                  </Autocomplete>
                ) : (
                  <Select
                    className='bg-white rounded e2g-form-input border-0'
                    id="fromAirport"
                    options={fromAirports}
                    onChange={option => setFieldValue('fromAirport', option)}
                    placeholder={t("Select Airport")}
                    isDisabled={!selectedFromCountry}
                  />
                )}
                {errors.fromAddress && touched.fromAddress ? (
                  <div className="e2g-error-text text-danger">{errors.fromAddress}</div>
                ) : null}
                {errors.fromAirport && touched.fromAirport ? (
                  <div className="e2g-error-text text-danger">{errors.fromAirport}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label mb-2 fs-custom fw-bold text-dark e2g-form-size" for="toLocation">{t("To")}</Label>
                <FormGroup switch>
                  <Toggle
                    checked={deliveredToRecipient}
                    onChange={(value) => {
                      setFieldValue('deliveredToRecipient', value);
                      setDeliveredToRecipient(value);
                    }}
                  />
                  <Label check className='fs-7'>{t("Delivered to Recipient")}</Label>
                </FormGroup>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Select
                  className='bg-white rounded e2g-form-input border-0'
                  id="toLocation"
                  options={airportCountries}
                  onChange={option => handleCountryChange('toLocation', option, setFieldValue)}
                  placeholder={t("Select Location")}
                />
                {errors.toLocation && touched.toLocation ? (
                  <div className="e2g-error-text text-danger">{errors.toLocation}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                {deliveredToRecipient ? (
                  <Autocomplete
                  key={'to'+values?.toLocation?.value}
                    onLoad={(autocomplete) => (toCityZipRef.current = autocomplete)}
                    onPlaceChanged={() =>
                      handlePlaceSelect(toCityZipRef.current, setFieldValue, 'toAddress')
                    }
                    options={{
                      types: ['address'], // Restrict to address results
                      componentRestrictions: {
                        country: values.toLocation ? values?.toLocation?.value : '',
                      },
                    }}
                  >
                    <Field
                      name="toAddress"
                      as={Input}
                      placeholder={t("Provide Full Address")}
                      disabled={!(values?.toLocation?.value)}
                    />
                  </Autocomplete>
                ) : (
                  <Select
                    className='bg-white rounded e2g-form-input border-0'
                    id="toAirport"
                    options={toAirports}
                    onChange={option => setFieldValue('toAirport', option)}
                    placeholder={t("Select Airport")}
                    isDisabled={!selectedToCountry}
                  />
                )}
                {errors.toAddress && touched.toAddress ? (
                  <div className="e2g-error-text text-danger">{errors.toAddress}</div>
                ) : null}
                {errors.toAirport && touched.toAirport ? (
                  <div className="e2g-error-text text-danger">{errors.toAirport}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="12" className='text-end e2g-form-btn'>
              <Button type="submit" color="dark" className="btn btn-dark rounded-pill py-2 px-4 e2g-land-btn">
                {t('Get a spot offer')}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default AirForm;
